import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/api";
import { useLang } from "@/i18n";
import { ClientLayout } from "./ClientHome";
import { toast } from "sonner";
import { Send, MessageCircle, Mic, Square, Video } from "lucide-react";

export default function Chat() {
  const { t } = useLang();
  const [messages, setMessages] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const bottomRef = useRef(null);

  const load = () => api.get("/client/messages").then((r) => setMessages(r.data)).catch(() => setMessages([]));
  useEffect(() => {
    load();
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages?.length]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post("/client/messages", { text });
      setText("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        const ext = (rec.mimeType || "").includes("mp4") ? "m4a" : "webm";
        const fd = new FormData();
        fd.append("file", blob, `nota.${ext}`);
        try {
          await api.post("/client/messages/audio", fd);
          load();
        } catch (e) {
          toast.error(formatApiError(e));
        }
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      toast.error(t("chat.micDenied"));
    }
  };

  const stopRec = () => {
    recorderRef.current?.stop();
    setRecording(false);
  };

  return (
    <ClientLayout>
      <div className="max-w-xl mx-auto w-full flex flex-col gap-4" data-testid="chat-page">
        <div className="flex items-center gap-3">
          <img src="/coach-cartoon-wave.png" alt="" data-testid="coach-cartoon-chat"
            className="w-14 h-14 rounded-full object-cover border-2 border-primary/30 shrink-0" />
          <h1 className="text-3xl font-black uppercase tracking-tight flex items-center gap-2 flex-1">
            <MessageCircle className="w-7 h-7 text-primary" /> {t("chat.title")}
          </h1>
          <a href="https://meet.google.com/new" target="_blank" rel="noopener noreferrer" data-testid="chat-videocall-button"
            className="kc-btn-ghost !px-3 !py-2 inline-flex items-center gap-1.5 text-xs font-bold text-primary">
            <Video className="w-4 h-4" /> Meet
          </a>
        </div>
        <div className="kc-card p-4 min-h-[300px] max-h-[55vh] overflow-y-auto space-y-2" data-testid="chat-thread">
          {messages === null ? (
            <p className="text-muted-foreground text-sm">{t("common.loading")}</p>
          ) : messages.length === 0 ? (
            <p className="text-muted-foreground text-sm" data-testid="chat-empty">{t("chat.empty")}</p>
          ) : (
            messages.map((m) => (
              <div key={m._id} data-testid="chat-message"
                className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${
                  m.sender === "client" ? "bg-primary text-primary-foreground ml-auto" : "bg-secondary"
                }`}>
                {m.audio_url ? (
                  <audio controls preload="none" src={m.audio_url} className="w-52 max-w-full" data-testid="chat-audio-player" />
                ) : (
                  <p className="whitespace-pre-wrap">{m.text}</p>
                )}
                <p className="text-[10px] opacity-70 font-mono mt-0.5">{m.created_at?.slice(5, 16).replace("T", " ")}</p>
              </div>
            ))
          )}
          <div ref={bottomRef} />
        </div>
        <div className="flex gap-2 sticky bottom-20">
          <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()} placeholder={t("chat.placeholder")} className="kc-input flex-1" />
          <button data-testid="chat-mic-button" onClick={recording ? stopRec : startRec}
            className={`w-12 h-12 rounded-full grid place-items-center border-2 transition-colors ${
              recording ? "bg-destructive border-destructive text-white animate-pulse" : "border-border text-muted-foreground hover:border-primary/60"
            }`}>
            {recording ? <Square className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
          </button>
          <button data-testid="chat-send-button" onClick={send} disabled={sending || !text.trim()} className="kc-btn-primary !px-5">
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </ClientLayout>
  );
}
